import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Smartphone, MessageSquare, Phone, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormSelect, FormTextarea, FormField } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import {
  renderTemplate,
  useBulkCreateRelances,
  useRelanceTemplates,
  type CreateRelanceInput,
} from '@/hooks/queries/useRelances';
import { supabase } from '@/lib/supabase';
import { formatDA, formatDateDZ } from '@/lib/format';
import type { EcheanceRow, RelanceCanal, RelanceNiveau } from '@/lib/database.types';
import type { FactureRecouvrementRow } from '@/hooks/queries/useRecouvrementEntreprises';
import { t } from '@/locales/i18n';

// Sprint 6.9.13 — Le dialog peut désormais relancer 2 sources distinctes :
// soit des echeances (legacy), soit des factures recouvrement_entreprises.
// On normalise via RelanceTarget pour partager le rendu de templates et l'INSERT.
export type RelanceTarget =
  | { kind: 'echeance'; row: EcheanceRow }
  | { kind: 'recouvrement'; row: FactureRecouvrementRow };

interface NormalizedTarget {
  id: string;
  reference: string;
  factureRef: string;
  tierId: string;
  montantDu: number;
  deviseCode: string;
  dateEcheance: string;
  source: 'echeance' | 'recouvrement';
}

function normalize(t: RelanceTarget): NormalizedTarget {
  if (t.kind === 'echeance') {
    return {
      id: t.row.id,
      reference: t.row.reference,
      factureRef: t.row.facture_ref ?? t.row.reference,
      tierId: t.row.tier_id,
      montantDu: Number(t.row.montant_du),
      deviseCode: t.row.devise_code,
      dateEcheance: t.row.date_echeance,
      source: 'echeance',
    };
  }
  const r = t.row;
  return {
    id: r.id,
    reference: r.reference_facture,
    factureRef: r.reference_facture,
    tierId: r.tier_id ?? '',
    montantDu: Math.max(0, Number(r.montant_ttc) - Number(r.montant_encaisse)),
    deviseCode: 'DZD',
    dateEcheance: r.date_formation ?? r.created_at,
    source: 'recouvrement',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  targets: RelanceTarget[];
  entrepriseId: string;
  onSent: () => void;
}

const CANAL_ICONS: Record<RelanceCanal, JSX.Element> = {
  email: <Mail className="h-4 w-4" />,
  whatsapp: <Smartphone className="h-4 w-4" />,
  sms: <MessageSquare className="h-4 w-4" />,
  telephone: <Phone className="h-4 w-4" />,
  courrier: <FileText className="h-4 w-4" />,
  mise_en_demeure_pdf: <FileText className="h-4 w-4" />,
};

export function RelancerDialog({
  open,
  onOpenChange,
  targets,
  entrepriseId,
  onSent,
}: Props): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const { data: templates = [] } = useRelanceTemplates(entrepriseId);
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const bulkCreate = useBulkCreateRelances();

  const normalized = useMemo(() => targets.map(normalize), [targets]);

  const [niveau, setNiveau] = useState<RelanceNiveau>('1ere');
  const [canal, setCanal] = useState<RelanceCanal>('email');
  const [previewIndex, setPreviewIndex] = useState(0);
  const [overrideContent, setOverrideContent] = useState<string | null>(null);
  const [overrideSujet, setOverrideSujet] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const tierMap = useMemo(() => {
    const m = new Map<string, (typeof tiers)[number]>();
    for (const tt of tiers) m.set(tt.id, tt);
    return m;
  }, [tiers]);

  const entreprise = entreprises.find((e) => e.id === entrepriseId);
  const template = templates.find((tp) => tp.niveau === niveau && tp.canal === canal);

  useEffect(() => {
    if (!open) {
      setOverrideContent(null);
      setOverrideSujet(null);
      setPreviewIndex(0);
      setNiveau('1ere');
      setCanal('email');
    }
  }, [open]);

  const renderForTarget = (
    tg: NormalizedTarget,
  ): { sujet: string; contenu: string; dest: string } => {
    const tier = tierMap.get(tg.tierId);
    const vars = {
      tier_nom: tier?.raison_sociale ?? '',
      facture_ref: tg.factureRef,
      montant_du: formatDA(tg.montantDu).replace(' DA', ''),
      devise: tg.deviseCode,
      date_echeance: formatDateDZ(tg.dateEcheance),
      entreprise_nom: entreprise?.nom ?? 'KOST',
    };
    const tplSujet = overrideSujet ?? template?.sujet ?? '';
    const tplContenu = overrideContent ?? template?.contenu ?? '';
    const dest =
      canal === 'email'
        ? (tier?.email ?? '')
        : canal === 'whatsapp' || canal === 'sms' || canal === 'telephone'
          ? (tier?.telephone ?? '')
          : (tier?.adresse ?? '');
    return {
      sujet: renderTemplate(tplSujet, vars),
      contenu: renderTemplate(tplContenu, vars),
      dest,
    };
  };

  const target = normalized[previewIndex];
  const preview = target ? renderForTarget(target) : null;

  const handleSend = async (): Promise<void> => {
    if (normalized.length === 0) return;
    setSending(true);
    try {
      // Build records — la cible non utilisée reste null (XOR contrainte DB).
      const records: CreateRelanceInput[] = normalized.map((tg) => {
        const r = renderForTarget(tg);
        return {
          entreprise_id: entrepriseId,
          echeance_id: tg.source === 'echeance' ? tg.id : null,
          recouvrement_id: tg.source === 'recouvrement' ? tg.id : null,
          tier_id: tg.tierId,
          niveau,
          canal,
          sujet: r.sujet || null,
          contenu: r.contenu,
          destinataire: r.dest || null,
          statut: 'envoyee' as const,
          sent_at: new Date().toISOString(),
        };
      });

      // Si email : appeler edge function send-email-relance (best-effort, on log si KO)
      if (canal === 'email') {
        try {
          await supabase.functions.invoke('send-email-relance', {
            body: {
              entreprise_id: entrepriseId,
              messages: records.map((r) => ({
                to: r.destinataire,
                subject: r.sujet,
                content: r.contenu,
                echeance_id: r.echeance_id,
                recouvrement_id: r.recouvrement_id,
              })),
            },
          });
        } catch {
          /* edge function pas configurée — on continue, on enregistre quand même la trace */
        }
      }

      // Si WhatsApp : on ouvre les liens wa.me en série (premier seul si bulk pour ne pas spam)
      if (canal === 'whatsapp' && target && normalized.length === 1) {
        const tier = tierMap.get(target.tierId);
        const phone = (tier?.telephone ?? '').replace(/[^0-9]/g, '');
        if (phone) {
          const text = encodeURIComponent(preview?.contenu ?? '');
          window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
        }
      }

      const r = await bulkCreate.mutateAsync(records);
      if (r.errors.length === 0) {
        toast.success(`${r.success} relance(s) enregistrée(s)`);
        onSent();
      } else {
        toast.warning(`Erreurs : ${r.errors[0]}`);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {normalized.length === 1
              ? `Relancer ${normalized[0]?.reference}`
              : `Relancer ${normalized.length} factures`}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un canal et un niveau. Le message est rendu à partir d&apos;un template
            personnalisable.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormSelect
              label="Niveau"
              value={niveau}
              onChange={(v) => setNiveau(v as RelanceNiveau)}
              options={(
                ['1ere', '2eme', 'mise_en_demeure', 'contentieux', 'vip_soft'] as RelanceNiveau[]
              ).map((n) => ({ value: n, label: t(`echeances.relances.niveau.${n}`) }))}
            />
            <FormSelect
              label="Canal"
              value={canal}
              onChange={(v) => setCanal(v as RelanceCanal)}
              options={(
                ['email', 'whatsapp', 'sms', 'telephone', 'courrier'] as RelanceCanal[]
              ).map((c) => ({ value: c, label: t(`echeances.relances.canal.${c}`) }))}
            />
          </div>

          {normalized.length > 1 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Aperçu {previewIndex + 1} / {normalized.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={previewIndex === 0}
                onClick={() => setPreviewIndex((i) => i - 1)}
              >
                ‹
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={previewIndex >= normalized.length - 1}
                onClick={() => setPreviewIndex((i) => i + 1)}
              >
                ›
              </Button>
              <span className="ml-auto text-muted-foreground">
                {target ? target.reference : ''}
              </span>
            </div>
          )}

          {!template && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
              Aucun template défini pour {niveau} / {canal}. Vous pouvez en créer un dans
              l&apos;onglet «&nbsp;Personnaliser les templates&nbsp;» ou écrire le message
              ci-dessous.
            </div>
          )}

          {(canal === 'email' || canal === 'mise_en_demeure_pdf') && (
            <FormField
              label="Sujet"
              value={preview?.sujet ?? ''}
              onChange={(e) => setOverrideSujet(e.target.value)}
              fullWidth
            />
          )}

          <FormTextarea
            label="Message"
            value={preview?.contenu ?? ''}
            onChange={(e) => setOverrideContent(e.target.value)}
            rows={10}
            fullWidth
          />
          {target && preview && (
            <div className="text-xs text-muted-foreground">
              Destinataire : <span className="font-medium">{preview.dest || '—'}</span>
              {!preview.dest && ' (manquant — pensez à compléter la fiche tiers)'}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSend} disabled={sending || normalized.length === 0}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="ml-1 inline-flex items-center gap-1">
              {CANAL_ICONS[canal]}
              {normalized.length > 1
                ? `Envoyer ${normalized.length} relances`
                : 'Envoyer la relance'}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
